import { Controller } from "@hotwired/stimulus"

// A segmented code field: one box per character, the way a verification code
// is shown everywhere else people meet one.
//
// The boxes are the interface; the real value lives in a single hidden input,
// so the form still posts `user_code` exactly as Doorkeeper expects and the
// page keeps working with JavaScript disabled — without the controller, the
// hidden input is a plain visible text field.
//
// Codes here are eight alphanumeric characters, generated uppercase, and
// people arrive holding them on another screen. That makes three behaviours
// non-negotiable: typing advances, pasting fills the whole thing, and
// backspace walks back. Everything else is detail.
export default class extends Controller {
  static targets = ["box", "boxes", "label", "value"]

  // The plain text input is what the page ships with, so the form works with
  // no JavaScript at all. The boxes only exist once this controller is running
  // — progressive enhancement rather than a field that silently posts nothing.
  connect() {
    this.boxesTarget.hidden = false
    this.valueTarget.classList.add("sr-only")
    this.valueTarget.setAttribute("tabindex", "-1")
    this.valueTarget.setAttribute("aria-hidden", "true")
    this.labelTarget.setAttribute("for", this.boxTargets[0].id)

    this.spread()
    this.sync()

    // Someone who followed the link from their terminal arrives with the code
    // already filled and should land on the button, not have their cursor
    // stolen back to the first box.
    const first = this.boxTargets.find((box) => !box.value)
    if (first && !this.valueTarget.value) first.focus()
  }

  // Fill the boxes from whatever the real field already holds — a prefilled
  // user_code from the link, or a value the browser restored.
  spread() {
    const characters = (this.valueTarget.value || "").toUpperCase().split("")
    this.boxTargets.forEach((box, index) => { box.value = characters[index] || "" })
  }

  input(event) {
    const box = event.target
    // Keep only the last character typed: typing into a filled box should
    // replace it rather than being silently ignored.
    box.value = box.value.replace(/[^a-z0-9]/gi, "").slice(-1).toUpperCase()
    if (box.value) this.advance(box, 1)
    this.sync()
  }

  keydown(event) {
    const box = event.target
    if (event.key === "Backspace" && !box.value) {
      // Backspace in an empty box clears the one before it and moves there,
      // which is what people expect from every other code field.
      event.preventDefault()
      const previous = this.neighbour(box, -1)
      if (previous) {
        previous.value = ""
        previous.focus()
        this.sync()
      }
      return
    }
    if (event.key === "ArrowLeft") { event.preventDefault(); this.advance(box, -1) }
    if (event.key === "ArrowRight") { event.preventDefault(); this.advance(box, 1) }
  }

  // Pasting is how most people get the code in — it came off another screen or
  // out of a terminal — so a paste anywhere fills the field from that point.
  paste(event) {
    event.preventDefault()
    const text = (event.clipboardData || window.clipboardData).getData("text")
    const characters = text.replace(/[^a-z0-9]/gi, "").toUpperCase().split("")
    const start = this.boxTargets.indexOf(event.target)
    characters.forEach((character, offset) => {
      const box = this.boxTargets[start + offset]
      if (box) box.value = character
    })
    const landing = this.boxTargets[Math.min(start + characters.length, this.boxTargets.length - 1)]
    landing?.focus()
    this.sync()
  }

  neighbour(box, direction) {
    return this.boxTargets[this.boxTargets.indexOf(box) + direction]
  }

  advance(box, direction) {
    this.neighbour(box, direction)?.focus()
  }

  // The boxes are only ever a view of the hidden value the form posts.
  sync() {
    this.valueTarget.value = this.boxTargets.map((box) => box.value).join("")
  }
}
